import { useEffect } from 'react';
import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { DatovkaApiType } from '@gov-nx/api';
import type { PersonCommunicationData } from '@gov-nx/core/hooks';
import { useAuthorizationConsentYupSchema } from '@gov-nx/core/hooks';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { Optional } from '@gov-nx/core/types';
import {
	getEntries,
	getKeys,
	getValues,
	is,
	pairs,
	prop,
} from '@gov-nx/core/types';
import { RadioBooleanOption } from '@gov-nx/ui/types';
import {
	dataBoxParams,
	getAttachmentsIds,
	today,
	toStringDate,
	useDataBoxShape,
	useDateShape,
	useEmailShape,
	usePhoneShape,
	useZipCodeShape,
} from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';
import type {
	DataRequest,
	TrestniOznameniFormData,
	TrestniOznameniFormDataStep1,
	TrestniOznameniFormDataStep2,
	TrestniOznameniFormDataStep3,
	TrestniOznameniFormDataStep4,
	TrestniOznameniFormDataStep5,
	TrestniOznameniFormDataStep6,
} from './service.types';
import {
	TrestniOznameniGender,
	TrestniOznameniGroupTarget,
} from './service.types';

const isAllFieldFalsy = (fields: Optional<boolean>[]) =>
	fields.every((f) => !f);

export interface FormInstanceProps {
	code: ServiceCode;
}

export function FormInstanceStep1({
	code,
}: FormInstanceProps): FormDefinition<TrestniOznameniFormDataStep1> {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const groupTargetKeys = getKeys(TrestniOznameniGroupTarget);

	const restOfCheckboxes = (except: keyof typeof TrestniOznameniGroupTarget) =>
		groupTargetKeys.filter((r) => r !== except);
	const checkboxRequiredMessage = tsn(
		'formular.validace.popis-skupiny.povinny'
	);

	const formSchema = yup
		.object()
		.shape(
			{
				popisProblemu: yup
					.string()
					.max(10000, tsn('formular.validace.popis-problemu.max'))
					.required(tsn('formular.validace.popis-problemu.povinny')),

				skupinyKtereSeOdlisujiUrcitymiTypickymiZnaky: yup
					.boolean()
					.optional()
					.when(
						restOfCheckboxes('skupinyKtereSeOdlisujiUrcitymiTypickymiZnaky'),
						(fields: boolean[], schema) => {
							if (isAllFieldFalsy(fields)) {
								return yup
									.boolean()
									.oneOf([true], checkboxRequiredMessage)
									.required(checkboxRequiredMessage);
							}
							return schema;
						}
					),
				skupinyKtereSeOdlisujiUrcitymiTypickymiZnakyPopis: yup
					.string()
					.optional()
					.when(
						'skupinyKtereSeOdlisujiUrcitymiTypickymiZnaky',
						([field], schema) => {
							if (field) {
								return yup
									.string()
									.max(250, tsn('formular.validace.skupina.max'))
									.required(tsn('formular.validace.skupina-popis.povinny'));
							}
							return schema;
						}
					),

				etnickeANarodnostiSkupiny: yup
					.boolean()
					.when(
						restOfCheckboxes('etnickeANarodnostiSkupiny'),
						(fields: boolean[], schema) => {
							if (isAllFieldFalsy(fields)) {
								return yup
									.boolean()
									.oneOf([true], checkboxRequiredMessage)
									.required(checkboxRequiredMessage);
							}
							return schema;
						}
					),
				etnickeANarodnostiSkupinyPopis: yup
					.string()
					.optional()
					.when('etnickeANarodnostiSkupiny', ([field], schema) => {
						if (field === true) {
							return yup
								.string()
								.max(250, tsn('formular.validace.skupina.max'))
								.required(tsn('formular.validace.skupina-popis.povinny'));
						}
						return schema;
					}),

				skupinyDefinovaneVyznanim: yup
					.boolean()
					.optional()
					.when(
						restOfCheckboxes('skupinyDefinovaneVyznanim'),
						(fields: boolean[], schema) => {
							if (isAllFieldFalsy(fields)) {
								return yup
									.boolean()
									.oneOf([true], checkboxRequiredMessage)
									.required(checkboxRequiredMessage);
							}
							return schema;
						}
					),
				skupinyDefinovaneVyznanimPopis: yup
					.string()
					.optional()
					.when('skupinyDefinovaneVyznanim', ([field], schema) => {
						if (field === true) {
							return yup
								.string()
								.max(250, tsn('formular.validace.skupina.max'))
								.required(tsn('formular.validace.skupina-popis.povinny'));
						}
						return schema;
					}),
				sexualniMensiny: yup
					.boolean()
					.optional()
					.when(
						restOfCheckboxes('sexualniMensiny'),
						(fields: boolean[], schema) => {
							if (isAllFieldFalsy(fields)) {
								return yup
									.boolean()
									.oneOf([true], checkboxRequiredMessage)
									.required(checkboxRequiredMessage);
							}
							return schema;
						}
					),
				sexualniMensinyPopis: yup
					.string()
					.optional()
					.when('sexualniMensiny', ([field], schema) => {
						if (field) {
							return yup
								.string()
								.max(250, tsn('formular.validace.skupina.max'))
								.required(tsn('formular.validace.skupina-popis.povinny'));
						}
						return schema;
					}),

				skupinyDefinovanePolitickymPresvedcenim: yup
					.boolean()
					.optional()
					.when(
						restOfCheckboxes('skupinyDefinovanePolitickymPresvedcenim'),
						(fields: boolean[], schema) => {
							if (isAllFieldFalsy(fields)) {
								return yup
									.boolean()
									.oneOf([true], checkboxRequiredMessage)
									.required(checkboxRequiredMessage);
							}
							return schema;
						}
					),
				skupinyDefinovanePolitickymPresvedcenimPopis: yup
					.string()
					.optional()
					.when(
						'skupinyDefinovanePolitickymPresvedcenim',
						([field], schema) => {
							if (field === true) {
								return yup
									.string()
									.max(250, tsn('formular.validace.skupina.max'))
									.required(tsn('formular.validace.skupina-popis.povinny'));
							}
							return schema;
						}
					),
				jine: yup
					.boolean()
					.optional()
					.when(restOfCheckboxes('jine'), (fields: boolean[], schema) => {
						if (isAllFieldFalsy(fields)) {
							return yup
								.boolean()
								.oneOf([true], checkboxRequiredMessage)
								.required(checkboxRequiredMessage);
						}
						return schema;
					}),
				jinePopis: yup
					.string()
					.optional()
					.when('jine', ([field], schema) => {
						if (field === true) {
							return yup
								.string()
								.max(250, tsn('formular.validace.skupina.max'))
								.required(tsn('formular.validace.skupina-popis.povinny'));
						}
						return schema;
					}),
			},
			pairs(groupTargetKeys)
		)
		.required();

	const formMethods = useGovForm<TrestniOznameniFormDataStep1>({
		formSchema,
		defaultValues: {
			popisProblemu: undefined,

			skupinyKtereSeOdlisujiUrcitymiTypickymiZnaky: false,
			skupinyKtereSeOdlisujiUrcitymiTypickymiZnakyPopis: undefined,

			etnickeANarodnostiSkupiny: false,
			etnickeANarodnostiSkupinyPopis: undefined,

			skupinyDefinovaneVyznanim: false,
			skupinyDefinovaneVyznanimPopis: undefined,

			sexualniMensiny: false,
			sexualniMensinyPopis: undefined,

			skupinyDefinovanePolitickymPresvedcenim: false,
			skupinyDefinovanePolitickymPresvedcenimPopis: undefined,

			jine: false,
			jinePopis: undefined,
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export function FormInstanceStep2({
	code,
}: FormInstanceProps): FormDefinition<TrestniOznameniFormDataStep2> {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const { getDatePickerShape } = useDateShape();
	const tsn = getLocalizeCurried(code);

	const formSchema = yup
		.object({
			datumZjisteni: getDatePickerShape().max(
				today(),
				tsn('formular.validace.datum-zjisteni.max')
			),

			verejnePristupny: yup.string().oneOf(getKeys(RadioBooleanOption)),
			verejnePristupnyKomu: yup
				.string()
				.optional()
				.when('verejnePristupny', ([field], schema) => {
					if (field === RadioBooleanOption.yes) {
						return yup
							.string()
							.max(250, tsn('formular.validace.verejne-pristupny.max'));
					}
					return schema;
				}),
			printscreen: yup.array().optional(),
			priloha: yup.array().optional(),
		})
		.required();

	const formMethods = useGovForm<TrestniOznameniFormDataStep2>({
		formSchema,
		defaultValues: {
			datumZjisteni: undefined,
			verejnePristupny: undefined,
			verejnePristupnyKomu: undefined,
			printscreen: undefined,
			priloha: undefined,
		},
	});
	const publicAvailable = formMethods.watch('verejnePristupny');

	useEffect(() => {
		if (publicAvailable === RadioBooleanOption.no) {
			formMethods.setValue('verejnePristupnyKomu', '');
			formMethods.resetField('verejnePristupnyKomu');
		}
	}, [formMethods, publicAvailable]);

	return getFormDefinition({ formMethods, formSchema });
}

export function FormInstanceStep3({
	code,
}: FormInstanceProps): FormDefinition<TrestniOznameniFormDataStep3> {
	const { getZipCodeShape } = useZipCodeShape();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);

	const formSchema: yup.ObjectSchema<TrestniOznameniFormDataStep3> = yup
		.object({
			jePachatelZnam: yup
				.string()
				.oneOf(getValues(RadioBooleanOption))
				.required(tsn('formular.validace.je-pachatel-znam.povinny')),
			pachatelJmenoPrijmeni: yup
				.string()
				.max(250, tsn('formular.validace.pachatel-info.max'))
				.optional(),
			pachatelUlice: yup
				.string()
				.max(250, tsn('formular.validace.pachatel-info.max'))
				.optional(),
			pachatelObec: yup
				.string()
				.max(250, tsn('formular.validace.pachatel-info.max'))
				.optional(),
			pachatelPsc: getZipCodeShape().optional(),
			pachatelStatniPrislusnost: yup.string().optional(),
			pachatelPohlavi: yup
				.string()
				.oneOf(getValues(TrestniOznameniGender))
				.optional(),
			pachatelVek: yup
				.number()
				.min(0, tsn('formular.validace.vek.min'))
				.optional(),
			pachatelZjisten: yup
				.string()
				.max(10000, tsn('formular.validace.pachatel-zjisten.max'))
				.optional(),
			pachatelLink: yup
				.string()
				.max(250, tsn('formular.validace.pachatel-info.max'))
				.optional(),
			pachatelJine: yup
				.string()
				.max(10000, tsn('formular.validace.pachatel-jine.max'))
				.optional(),
		})
		.required();

	const formMethods = useGovForm<TrestniOznameniFormDataStep3>({
		formSchema,
		defaultValues: {
			jePachatelZnam: undefined,
			pachatelJmenoPrijmeni: undefined,
			pachatelUlice: undefined,
			pachatelObec: undefined,
			pachatelPsc: undefined,
			pachatelStatniPrislusnost: undefined,
			pachatelPohlavi: undefined,
			pachatelVek: undefined,
			pachatelZjisten: undefined,
			pachatelLink: undefined,
			pachatelJine: undefined,
		},
	});

	const isPerpetratorKnown = formMethods.watch('jePachatelZnam');

	useEffect(() => {
		if (isPerpetratorKnown === RadioBooleanOption.no) {
			formMethods.reset({
				jePachatelZnam: RadioBooleanOption.no,
			});
		}
	}, [formMethods, isPerpetratorKnown]);

	return getFormDefinition({ formMethods, formSchema });
}

export function FormInstanceStep4({
	code,
}: FormInstanceProps): FormDefinition<TrestniOznameniFormDataStep4> {
	const { getZipCodeShape } = useZipCodeShape();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);

	const formSchema: yup.ObjectSchema<TrestniOznameniFormDataStep4> = yup
		.object({
			jeObetZnama: yup
				.string()
				.oneOf(getValues(RadioBooleanOption))
				.required(tsn('formular.validace.je-obet-znama.povinny')),
			obetJmenoPrijmeni: yup
				.string()
				.max(250, tsn('formular.validace.obet-info.max'))
				.optional(),
			obetUlice: yup
				.string()
				.max(250, tsn('formular.validace.obet-info.max'))
				.optional(),
			obetObec: yup
				.string()
				.max(250, tsn('formular.validace.obet-info.max'))
				.optional(),
			obetPsc: getZipCodeShape().optional(),
			obetPohlavi: yup
				.string()
				.oneOf(getValues(TrestniOznameniGender))
				.optional(),
			obetVek: yup.number().min(0, tsn('formular.validace.vek.min')).optional(),
			obetJine: yup
				.string()
				.max(10000, tsn('formular.validace.obet-jine.max'))
				.optional(),
			obetLink: yup
				.string()
				.max(250, tsn('formular.validace.obet-info.max'))
				.optional(),
		})
		.required();

	const formMethods = useGovForm<TrestniOznameniFormDataStep4>({
		formSchema,
		defaultValues: {
			jeObetZnama: undefined,
			obetJmenoPrijmeni: undefined,
			obetUlice: undefined,
			obetObec: undefined,
			obetPsc: undefined,
			obetPohlavi: undefined,
			obetVek: undefined,
			obetJine: undefined,
			obetLink: undefined,
		},
	});

	const isVictimKnow = formMethods.watch('jeObetZnama');

	useEffect(() => {
		if (isVictimKnow === RadioBooleanOption.no) {
			formMethods.reset({
				jeObetZnama: RadioBooleanOption.no,
			});
		}
	}, [formMethods, isVictimKnow]);

	return getFormDefinition({ formMethods, formSchema });
}

interface FormInstanceStep5Props extends FormInstanceProps {
	personCommunicationData: PersonCommunicationData;
}

export function FormInstanceStep5({
	personCommunicationData,
}: FormInstanceStep5Props): FormDefinition<TrestniOznameniFormDataStep5> {
	const { getPhoneShape } = usePhoneShape();
	const { getEmailShape } = useEmailShape();

	const formSchema = yup
		.object({
			zadostOVyrozumeni: yup
				.string()
				.oneOf(getKeys(RadioBooleanOption))
				.required(),
			_useKontaktTel: yup.boolean().optional(),
			kontaktTel: getPhoneShape(),
			_useKontaktEmail: yup.boolean().optional(),
			kontaktEmail: getEmailShape(),
		})
		.required();

	const formMethods = useGovForm<TrestniOznameniFormDataStep5>({
		formSchema,
		defaultValues: {
			_useKontaktEmail: !!personCommunicationData.emailVerified,
			_useKontaktTel: !!personCommunicationData.phoneVerified,
			zadostOVyrozumeni: RadioBooleanOption.yes,
			kontaktTel: personCommunicationData.phoneVerified,
			kontaktEmail: personCommunicationData.emailVerified,
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

interface Props {
	dataBoxTypes: DatovkaApiType['typSchranky'][];
	isLowAuthorization: boolean;
}

export function FormInstanceStep6({
	dataBoxTypes,
	isLowAuthorization,
}: Props): FormDefinition<TrestniOznameniFormDataStep6> {
	const { getDataBoxShape, getDataBoxDefaultValues } = useDataBoxShape({
		types: dataBoxTypes,
	});

	const consentSchema = useAuthorizationConsentYupSchema({
		isConsentRequired: isLowAuthorization,
	});

	const formSchema: yup.ObjectSchema<TrestniOznameniFormDataStep6> = yup
		.object({
			...getDataBoxShape({ isRequired: true }),
		})
		.shape({ __souhlas: consentSchema })
		.required();

	const formMethods = useGovForm<TrestniOznameniFormDataStep6>({
		formSchema,
		defaultValues: {
			...getDataBoxDefaultValues(),
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

const prepareCheckboxes = (
	formData: TrestniOznameniFormData
): { name: TrestniOznameniGroupTarget; description?: string }[] =>
	getEntries(TrestniOznameniGroupTarget)
		.map(([name, value]) =>
			formData[name]
				? { name: value, description: formData[`${name}Popis`] }
				: undefined
		)
		.filter(is);

export const prepareSubmitData = (
	formData: TrestniOznameniFormData
): DataRequest => {
	const checkboxes = prepareCheckboxes(formData);

	const { datovaSchrankaId, ...params } = dataBoxParams(formData);

	return {
		params: {
			odesilatelDSId: datovaSchrankaId,
			...params,
		},
		body: {
			kodPodani: 'HATECRIME2',

			popisProblemu: formData.popisProblemu,
			vedenoProtiSkupine: checkboxes.map(prop('name')),
			vedenoProtiSkupinePopis: checkboxes.map(prop('description')).filter(is),

			datumZjisteni: formData.datumZjisteni
				? toStringDate(formData.datumZjisteni)
				: undefined,
			verejnePristupny: formData.verejnePristupny === RadioBooleanOption.yes,
			verejnePristupnyKomu: formData.verejnePristupnyKomu,
			priloha: getAttachmentsIds(formData.priloha),
			printscreen: getAttachmentsIds(formData.printscreen),

			pachatelJmenoPrijmeni: formData.pachatelJmenoPrijmeni,
			pachatelUlice: formData.pachatelUlice,
			pachatelObec: formData.pachatelObec,
			pachatelPsc: formData.pachatelPsc,
			pachatelStatniPrislusnost: formData.pachatelStatniPrislusnost,
			pachatelPohlavi: formData.pachatelPohlavi,
			pachatelVek: formData.pachatelVek,
			pachatelZjisten: formData.pachatelZjisten,
			pachatelLink: formData.pachatelLink,
			pachatelJine: formData.pachatelJine,

			obetJmenoPrijmeni: formData.obetJmenoPrijmeni,
			obetUlice: formData.obetUlice,
			obetObec: formData.obetObec,
			obetPsc: formData.obetPsc,
			obetPohlavi: formData.obetPohlavi,
			obetVek: formData.obetVek,
			obetJine: formData.obetJine,
			obetLink: formData.obetLink,

			zadostOVyrozumeni: formData.zadostOVyrozumeni === RadioBooleanOption.yes,
			kontaktTel: formData._useKontaktTel ? formData.kontaktTel : undefined,
			kontaktEmail: formData._useKontaktEmail
				? formData.kontaktEmail
				: undefined,
		},
	};
};
